import { useTranslation } from "react-i18next";
import { SaveBar, DetailTabPanel } from "@/components/common";
import { useAuthStore } from "@/store/authStore";
import { preventImplicitSubmitOnEnter } from "@/lib/formKeyboard";
import {
  HouseCommissionReadOnlySection,
  HouseLocationSection,
} from "./HouseDetailSections";
import { HouseHeader } from "./HouseHeader";
import { HouseDetailTabNav } from "./HouseDetailTabNav";
import { useHouseDetailTab } from "./useHouseDetailTab";
import { HouseOverviewStats } from "./HouseOverviewStats";
import { HouseDevicesSection } from "./HouseDevicesSection";
import { useHouseHostProfileForm } from "./useHouseProfileForm";
import type { House } from "@/types";

interface HouseHostProfileFormProps {
  house: House;
  houseId: string;
}

/** Host (or read-mostly) view: tablets and guests first, address under settings. */
export function HouseHostProfileForm({ house, houseId }: HouseHostProfileFormProps) {
  const { t } = useTranslation("houses");
  const isHost = useAuthStore((s) => s.isHost());
  const [activeTab, setActiveTab] = useHouseDetailTab(false);

  const {
    formId,
    register,
    handleSubmit,
    setValue,
    watch,
    errors,
    nameModified,
    locationModified,
    setAddressValidation,
    onSubmit,
    handleRevert,
    saveBarPhase,
    confirmModal,
    isPending,
  } = useHouseHostProfileForm(house, houseId);

  return (
    <div className="w-full min-w-0" onKeyDown={preventImplicitSubmitOnEnter}>
      <form id={formId} className="w-full min-w-0" onSubmit={handleSubmit(onSubmit)}>
        <HouseHeader
          house={house}
          nameInputProps={register("name")}
          nameError={errors.name?.message}
          nameModified={nameModified}
          metrics={
            <HouseOverviewStats
              house={house}
              houseId={houseId}
              showHostShare={!isHost}
              showTelemetry={!isHost}
              onOpenDevices={() => setActiveTab("devices")}
              onOpenGuests={() => setActiveTab("devices")}
            />
          }
        />

        <HouseDetailTabNav activeTab={activeTab} onTabChange={setActiveTab} />

        <DetailTabPanel
          id="house-tabpanel-general"
          labelledBy="house-tab-general"
          isActive={activeTab === "general"}
          className="space-y-5"
        >
          <HouseLocationSection
            value={{
              address: watch("address"),
              coordinates: watch("coordinates"),
            }}
            onChange={(next) => {
              setValue("address", next.address, { shouldDirty: true });
              setValue("coordinates", next.coordinates, { shouldDirty: true });
            }}
            error={errors.address?.message}
            syncKey={`${house.id}-${house.updated_at}`}
            modified={locationModified}
            title={t("adminDetail.locationTitle")}
            onValidationChange={setAddressValidation}
          />

          {!isHost ? (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <HouseCommissionReadOnlySection house={house} />
            </div>
          ) : null}
        </DetailTabPanel>
      </form>

      <DetailTabPanel
        id="house-tabpanel-devices"
        labelledBy="house-tab-devices"
        isActive={activeTab === "devices"}
      >
        <HouseDevicesSection houseId={houseId} isHost={isHost} />
      </DetailTabPanel>

      <SaveBar
        phase={saveBarPhase}
        isPending={isPending}
        formId={formId}
        onRevert={handleRevert}
      />
      {confirmModal}
    </div>
  );
}
